import { useCallback } from 'react';
import * as Yup from 'yup';
import { Appointment, BulkAssignListForm, MeterPointDetails } from '../types';
import { Resolver, ResolverResult } from 'react-hook-form';

export const useBulkAssignYupResolver = (validationSchema: Yup.ObjectSchema<object, MeterPointDetails, object, ''>, appointments: { [mprn: string]: Appointment }): Resolver<BulkAssignListForm> =>
  useCallback(
    async (data: BulkAssignListForm): Promise<ResolverResult<BulkAssignListForm>> => {
      const siteList = [];
      const invalidSiteList = [];

      // check for duplicate mpans and raise an error so we don't submit anything
      const duplicateMpans = data.siteList.map(record => record.mprn).filter((mprn, index, self) => self.indexOf(mprn) !== index);
      if (duplicateMpans.length > 0) {
        return {
          values: {},
          errors: {
            siteList: {
              type: 'duplicate',
              message: `Duplicate MPRNs found: ${duplicateMpans.join(', ')}`
            }
          }
        };
      }

      for (const siteDetails of data.siteList) {
        if (!siteDetails.mprn) continue;
        const appointment = appointments[siteDetails.mprn];
        let hasError = false;
        const errorDescriptions = [];
        if (appointment && appointment.active !== false && appointment.address?.postcode !== siteDetails.address?.postcode) {
          errorDescriptions.push(`Appointment postcode ${appointment.address?.postcode} does not match postcode ${siteDetails.address?.postcode}`);
          hasError = true;
        }

        try {
          await validationSchema.validate(siteDetails, { abortEarly: false });
        } catch (error) {
          hasError = true;
        }

        if (hasError) {
          invalidSiteList.push({
            siteDetails,
            errorDescription: errorDescriptions.join('\n')
          });
          continue;
        }
        siteList.push(siteDetails);
      }

      return {
        values: { ...data, siteList, invalidSiteList },
        errors: {}
      };
    },
    [appointments, validationSchema]
  );
